/**
 * Dark mode's backdrop behind the Knowledge Archive map: deep space.
 *
 * A generated nebula image (our own, made with the app's image model for
 * this page): violet, blue and pink gas, clouds only, framing the edges
 * around a calm, dark middle, which is where the lessons sit. There are no
 * background stars: the only stars in the sky are your concepts. It is dimmed
 * under a dark veil and darkened further towards the edges, so it is a
 * backdrop and never competes with the constellations, the labels or the
 * amber review dots.
 *
 * It drifts a little as you pan and zoom, much less than the map, which
 * gives the sky depth. Light mode keeps its plain card.
 */

import deepSpace from '@/assets/mindspace/deep-space.webp';

/** How far the sky moves for a pixel of map pan: far away, so very little. */
const DRIFT = 0.04;
/** The sky is drawn larger than the frame so drifting never shows an edge. */
const OVERSCAN = 1.12;

export function SpaceBackdrop({
  width,
  height,
  camera,
  rounded,
}: {
  width: number;
  height: number;
  /** The map's camera, for the drift: scale and the graph point at centre. */
  camera: { k: number; cx: number; cy: number };
  /** Match the frame's rounded corners. */
  rounded: boolean;
}) {
  // Pan in screen pixels is graph centre times scale; the sky moves a small,
  // bounded share of it the opposite way, like something very far off.
  const room = ((OVERSCAN - 1) / 2) * Math.min(width, height);
  const clamp = (v: number) => Math.max(-room, Math.min(room, v));
  const dx = clamp(-camera.cx * camera.k * DRIFT);
  const dy = clamp(-camera.cy * camera.k * DRIFT);

  return (
    <div
      aria-hidden='true'
      className={`pointer-events-none absolute inset-0 overflow-hidden bg-[#06070e] ${rounded ? 'rounded-lg' : ''}`}
    >
      <div
        className='absolute bg-cover bg-center'
        style={{
          backgroundImage: `url(${deepSpace})`,
          width: `${OVERSCAN * 100}%`,
          height: `${OVERSCAN * 100}%`,
          left: `${-((OVERSCAN - 1) / 2) * 100}%`,
          top: `${-((OVERSCAN - 1) / 2) * 100}%`,
          transform: `translate3d(${dx}px, ${dy}px, 0)`,
        }}
      />
      {/* The veil that keeps it a backdrop: an even dimming, then darker
          towards the edges so the frame's corners recede. */}
      <div
        className='absolute inset-0'
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(6, 7, 14, 0.5) 0%, rgba(6, 7, 14, 0.6) 55%, rgba(6, 7, 14, 0.78) 100%)',
        }}
      />
    </div>
  );
}
